import { Action, Reducer } from 'redux';
import { GlobalState, AppThunkAction } from '.';
import * as MessageStore from './Message';
import { createMessage } from '../models/Message';
import { ActionDefinition, DefaultActionDefinition, isApiError, Profile, Role } from '../models';
import { produce } from 'immer';
import { loginAccount, logoutAccount, setCurrentAccount } from '../auth/service';

export enum ActionTypes {
    LoginStart = '@@auth/LOGIN/START',
    LoginComplete = '@@auth/LOGIN/COMPLETE',
    LoginFailed = '@@auth/LOGIN/FAILED',
    LogoutStart = '@@auth/LOGOUT/START',
    LogoutComplete = '@@auth/LOGOUT/COMPLETE',
    LogoutFailed = '@@auth/LOGOUT/FAILED',
    ProfileStart = '@@auth/PROFILE/START',
    ProfileComplete = '@@auth/PROFILE/COMPLETE',
    ProfileFailed = '@@auth/PROFILE/FAILED',
    SwitchProfileStart = '@@auth/SWITCH_PROFILE/START',
    SwitchProfileComplete = '@@auth/SWITCH_PROFILE/COMPLETE',
    SwitchProfileFailed = '@@auth/SWITCH_PROFILE/FAILED'
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface AuthState {
    isAuthenticated: boolean;
    userName: string | null;
    error: Error | null;
    switch: ActionDefinition;
    logout: ActionDefinition;
    profile: Profile | null;
    roles: Array<Role>;
}

export const initialState: AuthState = {
    isAuthenticated: false,
    userName: null,
    error: null,
    switch: DefaultActionDefinition,
    logout: DefaultActionDefinition,
    profile: null,
    roles: []
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.


export interface LoginStart { type: ActionTypes.LoginStart }
export interface LoginComplete { type: ActionTypes.LoginComplete, username: string }
export interface LoginFailed { type: ActionTypes.LoginFailed, error: Error }

export interface LogoutStart { type: ActionTypes.LogoutStart }
export interface LogoutComplete { type: ActionTypes.LogoutComplete }
export interface LogoutFailed { type: ActionTypes.LogoutFailed, error: Error }

export interface ProfileStart { type: ActionTypes.ProfileStart }
export interface ProfileComplete { type: ActionTypes.ProfileComplete, profile: Profile, roles: Array<Role> }
export interface ProfileFailed { type: ActionTypes.ProfileFailed, error: Error }

export interface SwitchProfileStart { type: ActionTypes.SwitchProfileStart }
export interface SwitchProfileComplete { type: ActionTypes.SwitchProfileComplete, token: string }
export interface SwitchProfileFailed { type: ActionTypes.SwitchProfileFailed, error: Error }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type LoginActions = LoginStart | LoginComplete | LoginFailed;
export type LogoutActions = LogoutStart | LogoutComplete | LogoutFailed;
export type ProfileActions = ProfileStart | ProfileComplete | ProfileFailed;
export type SwitchProfileActions = SwitchProfileStart | SwitchProfileComplete | SwitchProfileFailed;

export type Actions =
    | LoginActions
    | LogoutActions
    | ProfileActions
    | SwitchProfileActions;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    loginFailed: (error: Error) => ({ type: ActionTypes.LoginFailed, error } as LoginFailed),
    loginComplete: (username: string) => ({ type: ActionTypes.LoginComplete, username } as LoginComplete),
    login: (): AppThunkAction<Actions | MessageStore.Actions> => (dispatch, getState) => {
        dispatch({ type: ActionTypes.LoginStart });

        loginAccount()
            .then(() => {
                console.log('login successful');
            })
            .catch((error) => {
                console.error(error);
                dispatch({ type: ActionTypes.LoginFailed, error });
                dispatch(MessageStore.actionCreators.show(createMessage('Error Logging In', error.message, error)));
            })
    },
    logout: (): AppThunkAction<Actions | MessageStore.Actions> => (dispatch, getState) => {
        dispatch({ type: ActionTypes.LogoutStart });

        logoutAccount()
            .then(() => {
                console.log('logout successful');
            })
            .catch((error) => {
                console.error(error);
                dispatch({ type: ActionTypes.LogoutFailed, error });
                dispatch(MessageStore.actionCreators.show(createMessage('Error Logging Out', error.message, error)));
            })
    },
    switchAccount: (username: string): AppThunkAction<Actions | MessageStore.Actions> => (dispatch, getState) => {
        dispatch({ type: ActionTypes.SwitchProfileStart });
        setCurrentAccount(username);
    },
    getProfile: (accessToken: string): AppThunkAction<Actions | MessageStore.Actions> => (dispatch, getState) => {
        dispatch({ type: ActionTypes.ProfileStart });

        fetch(`api/user/profile`, {
            method: 'GET',
            headers: new Headers({
                'Authorization': 'Bearer ' + accessToken
            })
        })
            .then(response => {
                if (response.status >= 300) {
                    throw new Error(`Response did not indicate success: ${response.status}`);
                }
                return response.json() as Promise<{ profile: Profile, roles: Role[] }>;
            })
            .then(data => {
                dispatch({ type: ActionTypes.ProfileComplete, profile: data.profile, roles: data.roles });
            })
            .catch((error) => {
                if (isApiError(error)) {
                    console.error('Error:', error.sourceError);
                    error.response.json().then((content: any) => {
                        dispatch({ type: ActionTypes.ProfileFailed, error: content.Message });
                        dispatch(MessageStore.actionCreators.show(createMessage('Error Loading User Profile', content.Message, error.sourceError)));
                    });
                } else {
                    console.error('Error:', error);
                    dispatch({ type: ActionTypes.ProfileFailed, error });
                    dispatch(MessageStore.actionCreators.show(createMessage('Error Loading User Profile', error.message, error)));
                }
            });
    }
};

// ----------------
// SELECTORS - These are functions that, given the application state, will access a particular piece of information
// from the current store

export const selectors = {
    getError: (state: GlobalState) => state.auth.error,
    getUsername: (state: GlobalState) => state.auth.userName,
    getSwitchProfile: (state: GlobalState) => state.auth.switch,
    getLogout: (state: GlobalState) => state.auth.logout,
    getProfile: (state: GlobalState) => state.auth.profile,
    getRoles: (state: GlobalState) => state.auth.roles
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<AuthState> = (state: AuthState | undefined, incomingAction: Action): AuthState => {
    if (state === undefined) {
        return initialState;
    }

    const action = incomingAction as Actions;
    switch (action.type) {
        case ActionTypes.LoginStart: {
            return state;
        }
        case ActionTypes.LoginComplete: {
            return produce(state, draft => {
                draft.userName = action.username;
                draft.error = null;
                draft.isAuthenticated = true;
            });
        }
        case ActionTypes.LoginFailed: {
            return produce(state, draft => {
                draft.userName = null;
                draft.error = action.error;
                draft.isAuthenticated = false;
            });
        }
        case ActionTypes.LogoutStart: {
            return produce(state, draft => {
                draft.logout.is = true;
            });
        }
        case ActionTypes.LogoutComplete:
        case ActionTypes.LogoutFailed: {
            return state;
        }
        case ActionTypes.ProfileStart: {
            return produce(state, draft => {
                draft.profile = null;
            });
        }
        case ActionTypes.ProfileComplete: {
            return produce(state, draft => {
                draft.profile = action.profile;
                draft.roles = action.roles;
            });
        }
        case ActionTypes.SwitchProfileStart: {
            return produce(state, draft => {
                draft.switch.is = true;
            });
        }
        case ActionTypes.SwitchProfileComplete: {
            return produce(state, draft => {
                draft.switch.is = false;
            });
        }
        case ActionTypes.SwitchProfileFailed: {
            return produce(state, draft => {
                draft.switch.is = false;
                draft.switch.error = action.error;
            });
        }
        default: {
            return state;
        }
    }
};
